// StudentAchievement.jsx
import React, { useEffect, useState } from 'react';
import API from './api';

function getSemiannualPeriod(date = new Date()) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return month <= 6 ? `${year}-01` : `${year}-07`;
}

function formatSemiannualLabel(period) {
  if (!period) return '';
  const [year, m] = period.split('-');
  if (m === '01') return `Jan–Jun ${year}`;
  if (m === '07') return `Jul–Dec ${year}`;
  return period;
}

// Helper za labelu discipline
function formatDisciplineLabel(d) {
  if (!d) return '';
  let base = d.service_type || '';
  if (d.name) base += ` – ${d.name}`;
  if (d.is_primary) base += ' (primary)';
  return base;
}

export default function StudentAchievement({ mode = 'teacher' }) {
  const isAdmin = mode === 'admin';

  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');

  const [disciplines, setDisciplines] = useState([]);
  const [selectedDisciplineId, setSelectedDisciplineId] = useState('');

  const [activeTab, setActiveTab] = useState('monthly'); // 'monthly' | 'semiannual'
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  const [achievementText, setAchievementText] = useState('');
  const [goalText, setGoalText] = useState('');

  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);

  // ---- Učitaj studente (admin svi, teacher samo svoje) ----
  useEffect(() => {
    (async () => {
      try {
        const res = await API.get('/achievements/students');
        const list = (res.data || []).slice().sort((a, b) => {
          const la = (a.last_name || '').localeCompare(b.last_name || '', 'en', { sensitivity: 'base' });
          if (la !== 0) return la;
          return (a.first_name || '').localeCompare(b.first_name || '', 'en', { sensitivity: 'base' });
        });
        setStudents(list);
      } catch (e) {
        console.error('Error loading students', e);
      }
    })();
  }, []);

  // ---- Kad promenimo studenta: povuci discipline iz student_disciplines ----
  useEffect(() => {
    setDisciplines([]);
    setSelectedDisciplineId('');
    setPeriods([]);
    setSelectedPeriod('');
    setAchievementText('');
    setGoalText('');

    if (!selectedStudent) return;

    (async () => {
      try {
        const res = await API.get(`/achievements/${selectedStudent}/disciplines`);
        const list = res.data || [];
        setDisciplines(list);

        if (list.length > 0) {
          const primary = list.find(d => d.is_primary);
          setSelectedDisciplineId(String(primary ? primary.discipline_id : list[0].discipline_id));
        } else {
          setSelectedDisciplineId('');
        }
      } catch (e) {
        console.error('Error loading disciplines for student', e);
      }
    })();
  }, [selectedStudent]);

  // ---- Učitaj periode za izabranog studenta + periodType + disciplinu (discipline_id OBAVEZAN) ----
  useEffect(() => {
    setPeriods([]);
    setSelectedPeriod('');
    setAchievementText('');
    setGoalText('');

    if (!selectedStudent || !activeTab || !selectedDisciplineId) return;

    setLoadingPeriods(true);
    (async () => {
      try {
        const now = new Date();
        const currentPeriod =
          activeTab === 'monthly'
            ? now.toISOString().slice(0, 7)
            : getSemiannualPeriod(now);

        const params = {
          discipline_id: selectedDisciplineId,
        };

        const res = await API.get(
          `/achievements/${selectedStudent}/${activeTab}/periods`,
          { params }
        );
        const ps = res.data || [];

        // obavezno ubaci current period kao prvi, ako ne postoji
        if (!ps.includes(currentPeriod)) ps.unshift(currentPeriod);

        setPeriods(ps);
        setSelectedPeriod(currentPeriod);
      } catch (e) {
        console.error('Error loading periods', e);
      } finally {
        setLoadingPeriods(false);
      }
    })();
  }, [selectedStudent, activeTab, selectedDisciplineId]);

  // ---- Učitaj achievement & goal tekst za konkretan period + disciplinu ----
  useEffect(() => {
    setAchievementText('');
    setGoalText('');

    if (!selectedStudent || !activeTab || !selectedPeriod || !selectedDisciplineId) return;

    setLoadingRecord(true);
    (async () => {
      try {
        const params = {
          discipline_id: selectedDisciplineId,
        };

        const res = await API.get(
          `/achievements/${selectedStudent}/${activeTab}/${selectedPeriod}`,
          { params }
        );
        const row = res.data || {};
        setAchievementText(row.achievement_text || '');
        setGoalText(row.goal_txt || '');
      } catch (e) {
        console.error('Error loading achievement record', e);
        setAchievementText('');
        setGoalText('');
      } finally {
        setLoadingRecord(false);
      }
    })();
  }, [selectedStudent, activeTab, selectedPeriod, selectedDisciplineId]);

  const handleSave = async () => {
    if (!selectedStudent || !activeTab || !selectedPeriod || !selectedDisciplineId) return;

    if (!achievementText.trim() && !goalText.trim()) {
      return; // oba prazna – nema šta da se sačuva
    }

    setSaving(true);
    try {
      const params = {
        discipline_id: selectedDisciplineId,
      };

      await API.post(
        `/achievements/${selectedStudent}/${activeTab}/${selectedPeriod}`,
        {
          achievement_text: achievementText,
          goal_txt: goalText,
        },
        { params }
      );

      setSuccessMsg('Saved successfully');
      setTimeout(() => setSuccessMsg(''), 2500);

      // osveži liste perioda (nije obavezno, ali lepo)
      const resPeriods = await API.get(
        `/achievements/${selectedStudent}/${activeTab}/periods`,
        { params }
      );
      setPeriods(resPeriods.data || []);

      // i ponovno učitaj trenutni zapis da bismo bili sigurni da je sve up-to-date
      const resRow = await API.get(
        `/achievements/${selectedStudent}/${activeTab}/${selectedPeriod}`,
        { params }
      );
      const data = resRow.data || {};
      setAchievementText(data.achievement_text || '');
      setGoalText(data.goal_txt || '');
    } catch (e) {
      console.error('Failed to save achievement', e);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectedDiscipline = disciplines.find(
    d => String(d.discipline_id) === String(selectedDisciplineId)
  );

  const currentLabel =
    activeTab === 'semiannual'
      ? formatSemiannualLabel(selectedPeriod || '')
      : (selectedPeriod || '');

  return (
    <div className="max-w-4xl mx-auto mt-6">
      {/* Header kartica */}
      <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-sky-600 to-blue-600 text-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Student Achievements & Goals
            </h2>
            <p className="text-xs sm:text-[13px] text-sky-100">
              Per discipline · Per period · Synced with Student Progress Report
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-3 py-1 rounded-full bg-white/10 border border-white/30">
              Mode: <span className="font-semibold">{isAdmin ? 'Admin' : 'Teacher'}</span>
            </span>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Student select */}
          <div>
            <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wide">
              Student
            </label>
            <select
              className="border border-slate-300 rounded-lg w-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              value={selectedStudent}
              onChange={e => setSelectedStudent(e.target.value)}
            >
              <option value="">-- Choose student --</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>
                  {(s.last_name || '').trim()}, {(s.first_name || '').trim()}
                </option>
              ))}
            </select>
          </div>

          {/* Discipline pills */}
          {selectedStudent && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Discipline
                </span>
              </div>

              {disciplines.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {disciplines.map(d => (
                    <button
                      key={d.discipline_id}
                      type="button"
                      onClick={() => setSelectedDisciplineId(String(d.discipline_id))}
                      className={
                        'px-3 py-1 rounded-full text-xs border transition-colors ' +
                        (String(selectedDisciplineId) === String(d.discipline_id)
                          ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                          : 'bg-slate-50 text-sky-700 border-slate-200 hover:bg-sky-50')
                      }
                    >
                      {d.service_type}
                      {d.name ? ` – ${d.name}` : ''}
                      {d.is_primary ? ' (primary)' : ''}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  This student has no disciplines assigned in <span className="font-semibold">student_disciplines</span>.
                  <br />
                  Please configure disciplines for this student in the admin panel.
                </div>
              )}
            </div>
          )}

          {/* Period type tabs */}
          {selectedStudent && disciplines.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex space-x-2 bg-slate-100 p-1 rounded-full">
                <button
                  className={
                    'px-4 py-1.5 rounded-full text-xs font-medium ' +
                    (activeTab === 'monthly'
                      ? 'bg-white text-sky-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800')
                  }
                  onClick={() => setActiveTab('monthly')}
                >
                  Monthly
                </button>
                <button
                  className={
                    'px-4 py-1.5 rounded-full text-xs font-medium ' +
                    (activeTab === 'semiannual'
                      ? 'bg-white text-sky-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800')
                  }
                  onClick={() => setActiveTab('semiannual')}
                >
                  Semiannual
                </button>
              </div>

              <div className="text-[11px] text-slate-500">
                {selectedDiscipline && (
                  <>
                    <span className="font-semibold text-slate-700">Context:&nbsp;</span>
                    {formatDisciplineLabel(selectedDiscipline)}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Period select */}
          {selectedStudent && disciplines.length > 0 && (
            <div>
              <label className="block mb-1 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                {activeTab === 'monthly' ? 'Month' : 'Semiannual period'}
              </label>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <select
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  value={selectedPeriod}
                  onChange={e => setSelectedPeriod(e.target.value)}
                  disabled={loadingPeriods}
                >
                  <option value="">-- Choose period --</option>
                  {periods.map(p => (
                    <option key={p} value={p}>
                      {activeTab === 'semiannual' ? formatSemiannualLabel(p) : p}
                      {p === (activeTab === 'monthly'
                        ? new Date().toISOString().slice(0, 7)
                        : getSemiannualPeriod()) ? ' (current)' : ''}
                    </option>
                  ))}
                </select>

                {loadingPeriods && (
                  <span className="text-xs text-slate-500">Loading periods...</span>
                )}
              </div>
            </div>
          )}

          {/* Glavni editor zona */}
          {selectedStudent &&
            selectedPeriod &&
            disciplines.length > 0 &&
            selectedDisciplineId && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                      Achievements
                    </label>
                    <span className="text-[11px] text-slate-500">
                      {currentLabel}
                    </span>
                  </div>
                  <textarea
                    className="border border-slate-300 rounded-lg w-full p-2 text-sm min-h-[140px] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    value={achievementText}
                    onChange={e => setAchievementText(e.target.value)}
                    placeholder="Describe achievements for this student in the selected discipline and period..."
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                      Goals
                    </label>
                    <span className="text-[11px] text-slate-500">
                      {currentLabel}
                    </span>
                  </div>
                  <textarea
                    className="border border-slate-300 rounded-lg w-full p-2 text-sm min-h-[140px] focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    value={goalText}
                    onChange={e => setGoalText(e.target.value)}
                    placeholder="Set goals for this student in the selected discipline and period..."
                  />
                </div>
              </div>
            )}

          {/* Footer sa Save dugmetom i statusom */}
          {selectedStudent &&
            selectedPeriod &&
            disciplines.length > 0 &&
            selectedDisciplineId && (
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                    onClick={handleSave}
                    disabled={
                      saving ||
                      (!achievementText.trim() && !goalText.trim()) ||
                      loadingRecord
                    }
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  {loadingRecord && (
                    <span className="text-xs text-slate-500">Loading record...</span>
                  )}
                </div>

                <div className="text-xs text-right min-h-[1.3rem]">
                  {successMsg && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-xs">
                      {successMsg}
                    </span>
                  )}
                </div>
              </div>
            )}

          {!selectedStudent && (
            <div className="mt-2 text-xs text-slate-500">
              Choose a student to start editing achievements and goals.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}





